<script setup>
/**
 * CatalogFilters Component
 * Toggle filters for catalog display: out-of-stock, kosher, rabanut
 * Used in both desktop sidebar and mobile overlay
 */
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useCatalogStore } from '../../../app/store/catalog.js'

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
  showTutorial: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['navigate-not-in-assortment'])

const catalogStore = useCatalogStore()
const {
  filterShowOutOfStock,
  filterKosher,
  filterRabanut,
  filterShowNotInAssortment,
  notInAssortmentLoading,
  notInAssortmentLoaded,
  notInAssortmentItemsByMakat,
  canShowNotInAssortmentFilter,
} = storeToRefs(catalogStore)

const notInAssortmentCount = computed(() => notInAssortmentItemsByMakat.value.size)
const showBrowseButton = computed(
  () => notInAssortmentLoaded.value && notInAssortmentCount.value > 0
)

function toggleShowOutOfStock() {
  catalogStore.setFilter('showOutOfStock', !filterShowOutOfStock.value)
}

function toggleKosher() {
  catalogStore.setFilter('kosher', !filterKosher.value)
}

function toggleRabanut() {
  catalogStore.setFilter('rabanut', !filterRabanut.value)
}

function toggleShowNotInAssortment() {
  catalogStore.setFilter('showNotInAssortment', !filterShowNotInAssortment.value)
}
</script>

<template>
  <div
    v-tutorial="
      showTutorial
        ? {
            id: 'catalog-desktop-filters',
            text: 'סינון מוצרים לפי כשרות וזמינות במלאי — כדי לראות רק מה שרלוונטי לכם',
            importance: 'info',
            showOnce: true,
            autoTrigger: false,
            group: 'catalog-desktop-intro',
            step: 2,
          }
        : undefined
    "
    class="catalog-filters"
    :class="{ compact }"
  >
    <!-- Out of Stock Toggle -->
    <div class="filter-row">
      <span class="filter-label">הצג מוצרים חסרים במלאי</span>
      <button
        class="toggle-switch"
        :class="{ active: filterShowOutOfStock }"
        @click="toggleShowOutOfStock"
        role="switch"
        :aria-checked="filterShowOutOfStock"
      >
        <span class="toggle-knob" />
      </button>
    </div>

    <!-- Kosher Supervision Toggle -->
    <div class="filter-row">
      <span class="filter-label">מוצרים בהשגחת כשרות</span>
      <button
        class="toggle-switch"
        :class="{ active: filterKosher }"
        @click="toggleKosher"
        role="switch"
        :aria-checked="filterKosher"
      >
        <span class="toggle-knob" />
      </button>
    </div>

    <!-- Rabanut Approval Toggle (only when kosher is active) -->
    <Transition name="filter-slide">
      <div v-if="filterKosher" class="filter-row filter-row-nested">
        <span class="filter-label">באישור רבנות ראשית בלבד</span>
        <button
          class="toggle-switch"
          :class="{ active: filterRabanut }"
          @click="toggleRabanut"
          role="switch"
          :aria-checked="filterRabanut"
        >
          <span class="toggle-knob" />
        </button>
      </div>
    </Transition>

    <!-- Not In Assortment Toggle (only for client/agent with non-master assortment) -->
    <div v-if="canShowNotInAssortmentFilter" class="filter-row">
      <span class="filter-label">
        הצג מוצרים לא במגוון
        <span v-if="notInAssortmentLoading" class="loading-dot" />
      </span>
      <button
        class="toggle-switch"
        :class="{ active: filterShowNotInAssortment }"
        @click="toggleShowNotInAssortment"
        role="switch"
        :aria-checked="filterShowNotInAssortment"
      >
        <span class="toggle-knob" />
      </button>
    </div>

    <!-- Browse Not In Assortment Page -->
    <button
      v-if="showBrowseButton"
      class="browse-not-in-assortment-btn"
      @click="emit('navigate-not-in-assortment')"
    >
      ראה {{ notInAssortmentCount }} מוצרים לא במגוון
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.catalog-filters {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
}

.catalog-filters.compact {
  padding: var(--spacing-sm) var(--spacing-md);
  gap: 8px;
}

.filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.filter-row-nested {
  padding-right: var(--spacing-md);
}

.filter-label {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  user-select: none;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.toggle-switch.active {
  background: var(--color-primary, #283548);
  border-color: var(--color-primary, #283548);
}

.toggle-knob {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle-switch.active .toggle-knob {
  transform: translateX(-18px);
}

/* Transition for nested filter */
.filter-slide-enter-active,
.filter-slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.filter-slide-enter-from,
.filter-slide-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: -10px;
}

.filter-slide-enter-to,
.filter-slide-leave-from {
  opacity: 1;
  max-height: 40px;
  margin-top: 0;
}

/* Browse Not In Assortment Button */
.browse-not-in-assortment-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--color-info, #3b82f6);
  border-radius: var(--radius-md, 8px);
  color: var(--color-info, #3b82f6);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s ease;
}

.browse-not-in-assortment-btn:hover {
  background: var(--color-info, #3b82f6);
  color: white;
}

/* Loading dot indicator */
.loading-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: var(--color-info);
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
  animation: pulse-dot 1s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
</style>
